import React, { useState } from 'react';
import { Spin, Form, Input, Tag, Button as AntdButton, Modal, message } from 'antd';
import { format } from 'date-fns';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { useEmployeeDetail } from '../hooks/useEmployees';
import StatusBadge from '@/components/ui/StatusBadge';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import RfidRegisterModal from '@/components/attendance/RfidRegisterModal';
import FingerprintEnrollWizard from '@/components/attendance/FingerprintEnrollWizard';
import { deleteEsp32FingerprintSlot, registerEmployeeThumb } from '@services/attendance.service';
import { EMPLOYEES_QUERY_KEY, EMPLOYEE_DETAIL_QUERY_KEY } from '../hooks/employeesQueryKeys';
import {
    DetailLabel,
    DetailValue,
    DetailPanelLoading,
    DetailPanelContentWrap,
    DetailPanelScroll,
    DetailPanelFooter,
    DetailPanelFooterBtn,
    DetailValueInherit,
} from '@/pages/inside/tenant/style';

/** Format date as "9th March 2026" or "9th March 2026, 07:52:18" when time is present. */
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        const hasTime = dateStr.includes('T') || /\d{1,2}:\d{2}/.test(dateStr);
        return hasTime ? format(d, 'do MMMM yyyy, HH:mm:ss') : format(d, 'do MMMM yyyy');
    } catch {
        return dateStr;
    }
};

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

const formatWorkingDays = (days) => {
    if (!days) return '';
    const mapping = {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
    };
    return mapping[days.toLowerCase()] || capFirst(days);
};

/**
 * Content for the employee side panel. Use inside <SidePanel>.
 * Uses useEmployeeDetail from useEmployees hook file.
 * Optional: employee (from list); canUpdate, canRequestHrRemoval, canRemoveUser, hasPendingRemoval; onEdit, onRequestRemoval, onRemoveUser.
 */
const EmployeeDetailPanel = ({
    employeeId,
    employee: employeeFromList,
    canUpdate,
    canRequestHrRemoval,
    canRemoveUser,
    hasPendingRemoval,
    onEdit,
    onRequestRemoval,
    onRemoveUser,
}) => {
    const { data: employeeFromApi, isLoading, isError } = useEmployeeDetail(employeeId);
    const employee = employeeFromApi ?? employeeFromList;

    const queryClient = useQueryClient();
    const [rfidModalOpen, setRfidModalOpen] = useState(false);
    const [fingerprintModalOpen, setFingerprintModalOpen] = useState(false);
    const tenantId = useAuthStore((s) => s.user?.tenantId || s.user?.tenant_id);
    const [deleteFingerLoading, setDeleteFingerLoading] = useState(false);
    const esp32Ip = localStorage.getItem('code360_esp32_ip') || '';

    const handleDeleteFingerprint = async () => {
        if (!employee.thumbId) return;
        
        Modal.confirm({
            title: 'Delete Fingerprint Slot',
            content: `Are you sure you want to delete fingerprint Slot ${employee.thumbId} from the device and clear this mapping?`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                setDeleteFingerLoading(true);
                try {
                    if (esp32Ip) {
                        try {
                            await deleteEsp32FingerprintSlot(esp32Ip, employee.thumbId);
                            message.success('Fingerprint template deleted from physical device.');
                        } catch (err) {
                            console.warn('ESP32 Direct delete failed:', err);
                            message.warning('Could not connect to physical device to delete template. Clearing backend database mapping anyway.');
                        }
                    }
                    
                    await registerEmployeeThumb({ userId: employee.id, thumbId: "" }, tenantId);
                    message.success('Fingerprint mapping cleared from database.');
                    
                    queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
                    queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_QUERY_KEY(employee.id) });
                } catch (err) {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to clear fingerprint slot');
                } finally {
                    setDeleteFingerLoading(false);
                }
            }
        });
    };

    if (!employeeId) return null;
    if (isLoading && !employeeFromList) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }
    if (isError && !employee) {
        return (
            <Container $marginBottom="24px">
                <DetailValueInherit>Failed to load employee details.</DetailValueInherit>
            </Container>
        );
    }
    if (!employee) return null;

    const roles = employee.roles || [];
    const removalRequest = employee.removalRequest;
    const showRemovalStatus = removalRequest != null;
    const removalStatus = (removalRequest?.status || '').toLowerCase();
    const canRequestRemoval = canRequestHrRemoval && removalStatus !== 'pending';
    const showFooter = canUpdate || canRemoveUser || canRequestRemoval;

    return (
        <DetailPanelContentWrap>
            <DetailPanelScroll style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '12px', paddingBottom: '16px' }}>
                <Form layout="vertical">
                    <Form.Item label="Email" style={{ marginBottom: 16 }}>
                        <Input readOnly value={employee.email ?? '—'} />
                    </Form.Item>
                    <Form.Item label="Status" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', height: 44 }}>
                            <StatusBadge status={employee.status} />
                        </div>
                    </Form.Item>
                    <Form.Item label="Roles" style={{ marginBottom: 16 }}>
                        <Input readOnly value={roles.length === 0 ? '—' : roles.map((r) => r.name).join(', ')} />
                    </Form.Item>
                    <Form.Item label="Schedule Group" style={{ marginBottom: 16 }}>
                        <Input readOnly value={employee.scheduleGroup?.title ?? '—'} />
                    </Form.Item>
                    {employee.scheduleGroup && (
                        <>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <Form.Item label="Start Time" style={{ marginBottom: 16, flex: 1 }}>
                                    <Input readOnly value={employee.scheduleGroup.startTime || '—'} />
                                </Form.Item>
                                <Form.Item label="End Time" style={{ marginBottom: 16, flex: 1 }}>
                                    <Input readOnly value={employee.scheduleGroup.endTime || '—'} />
                                </Form.Item>
                            </div>
                            <Form.Item label="Working Days" style={{ marginBottom: 16 }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '6px 12px', minHeight: 44, borderRadius: 8, border: '1px solid var(--ant-color-border-outline, #d9d9d9)', background: 'var(--ant-color-bg-container-disabled, rgba(0, 0, 0, 0.04))', alignItems: 'center' }}>
                                    {employee.scheduleGroup.workingDays?.length ? (
                                        employee.scheduleGroup.workingDays.map((d) => (
                                            <Tag key={d} color="purple" style={{ margin: 0, padding: '2px 8px', borderRadius: 4 }}>
                                                {formatWorkingDays(d)}
                                            </Tag>
                                        ))
                                    ) : (
                                        <span style={{ color: 'rgba(0,0,0,0.45)' }}>—</span>
                                    )}
                                </div>
                            </Form.Item>
                        </>
                    )}
                    {showRemovalStatus && (
                        <Form.Item label="Removal Request" style={{ marginBottom: 16 }}>
                            <Input readOnly value={capFirst(removalStatus) || '—'} />
                        </Form.Item>
                    )}
                    <Form.Item label="RFID Card" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Input readOnly value={employee.rfid ?? 'Not Mapped'} style={{ flex: 1 }} />
                            {canUpdate && (
                                <Button type="button" onClick={() => setRfidModalOpen(true)} $padding="10px 16px" $size="14px">
                                    {employee.rfid ? 'Change' : 'Assign'}
                                </Button>
                            )}
                        </div>
                    </Form.Item>
                    <Form.Item label="Fingerprint" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Input readOnly value={employee.thumbId ? `Slot ${employee.thumbId}` : 'Not Enrolled'} style={{ flex: 1 }} />
                            {canUpdate && (
                                <Button type="button" onClick={() => setFingerprintModalOpen(true)} $padding="10px 16px" $size="14px">
                                    {employee.thumbId ? 'Re-enroll' : 'Enroll'}
                                </Button>
                            )}
                            {canUpdate && employee.thumbId && (
                                <Button
                                    type="button"
                                    onClick={handleDeleteFingerprint}
                                    disabled={deleteFingerLoading}
                                    style={{ background: '#ef4444', borderColor: '#ef4444', color: 'white' }}
                                    $padding="10px 16px"
                                    $size="14px"
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                    </Form.Item>
                    <Form.Item label="Last Login" style={{ marginBottom: 16 }}>
                        <Input readOnly value={formatDate(employee.lastLogin)} />
                    </Form.Item>
                    <Form.Item label="Created At" style={{ marginBottom: 16 }}>
                        <Input readOnly value={formatDate(employee.createdAt)} />
                    </Form.Item>
                </Form>
            </DetailPanelScroll>
            {showFooter && (
                <DetailPanelFooter style={{ justifyContent: 'flex-end', gap: '10px', paddingTop: '16px', marginTop: '12px' }}>
                    {canUpdate && (
                        <Button $primary={false} $size="14px" $padding="10px 16px" onClick={() => onEdit?.(employee)}>
                            Edit
                        </Button>
                    )}
                    {canRequestRemoval && !canRemoveUser && (
                        <AntdButton type="primary" danger style={{ height: 40, borderRadius: 8, fontWeight: 700, padding: '10px 16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => onRequestRemoval?.(employee)}>
                            Request removal
                        </AntdButton>
                    )}
                    {canRemoveUser && (
                        <AntdButton type="primary" danger style={{ height: 40, borderRadius: 8, fontWeight: 700, padding: '10px 16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => onRemoveUser?.(employee)}>
                            Remove User
                        </AntdButton>
                    )}
                </DetailPanelFooter>
            )}
            <RfidRegisterModal
                open={rfidModalOpen}
                onCancel={() => setRfidModalOpen(false)}
                userId={employee.id}
                userName={employee.name ?? employee.email}
                currentRfid={employee.rfid}
                tenantId={tenantId}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
                    queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_QUERY_KEY(employee.id) });
                }}
            />
            <FingerprintEnrollWizard
                open={fingerprintModalOpen}
                onCancel={() => setFingerprintModalOpen(false)}
                userId={employee.id}
                userName={employee.name ?? employee.email}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
                    queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_QUERY_KEY(employee.id) });
                }}
            />
        </DetailPanelContentWrap>
    );
};

export default EmployeeDetailPanel;
